/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-06-30
 */

import { useEffect, useState } from "react";
import { useQueryListContext } from "./QueryListContext";
import { Loading } from "./Loading";
import { Error } from "./Error";
import { ListControl } from "./ListControl";
import { ListSearch } from "./ListSearch";
import { ListColumns } from "./ListColumns";
import { ListRecords } from "./ListRecords";
import { Row, Col, Table, Card, Modal, Button } from "react-bootstrap";
import { Edit } from "./Edit";
import { useAuth } from "../../../contexts/AuthContext";
import { DynamicObject, IFrontEndOnly, IQueryGeneric, IQueryListComms, IQueryTableColumnDetails, ColumnSort, QueryTableListProperties } from "../Interfaces";
import { Download } from "lucide-react";
import ShowErrorAlert from "../../ShowErrorAlert";
import { TableLoader } from "./TableLoader";


export const QueryTableList: React.FC<QueryTableListProperties> = (props) => {

    

    function isTableQuery(q: IQueryGeneric): boolean {
      return q.tableName !== null;
    }

    function isSelectQuery(q: IQueryGeneric): boolean {
      return q.selectCode !== null;
    }

    function schemaBody(q: IQueryGeneric): Record<string, any > | null {
        if(isSelectQuery(q)){
          return q.params;
        }else{
          return null;
        }
    }

    function dataBody(q: IQueryGeneric): any {
        return { "where": q.where, "orderBy": q.orderBy, "params": q.params, "pageNum": q.pageNum, "pageSize": q.pageSize }; 
    }

    let queryOption = "";
    let schemeOption = "";
    let exportOption = ""; 

    if (isTableQuery(props.query)) {

        queryOption = `${process.env.REACT_APP_BASE_URL}/table/${props.query.tableName}/data?includeCount=true`;
        schemeOption = `${process.env.REACT_APP_BASE_URL}/table/${props.query.tableName}/schema`;  

    } else if (isSelectQuery(props.query)) {

        queryOption = `${process.env.REACT_APP_BASE_URL}/select/${props.query.appCode}/${props.query.selectCode}/data?includeCount=true`;
        schemeOption = `${process.env.REACT_APP_BASE_URL}/select/${props.query.appCode}/${props.query.selectCode}/schema`;  

    } else {
         //console.log(" Unknown Query Type");
    }

    //checking the report bits here
    if(props.reportOptions?.reportQuery){
        if(isTableQuery(props.reportOptions?.reportQuery)){
           exportOption = `${process.env.REACT_APP_BASE_URL}/table/${props.reportOptions?.reportQuery.tableName}/xlsx`; 
        }else if(isSelectQuery(props.reportOptions?.reportQuery)){
           exportOption = `${process.env.REACT_APP_BASE_URL}/select/${props.reportOptions?.reportQuery.appCode}/${props.reportOptions?.reportQuery.selectCode}/xlsx`; 
        }
    }

    const comms: IQueryListComms = { query: queryOption, schema: schemeOption }


    const { isAuthenticated, 
            token, 
            fetchData, 
            fetchDataMulti, 
            fetchDataMultiAdvanced, 
            responseErrorMessage, 
            showErrorAlert, 
            responseStatus, 
            userDetail, refreshTable}  = useAuth();


    const {ctxData, getData, setData } = useQueryListContext();


    const [columnDetails, setColumnDetails] = useState<IQueryTableColumnDetails[]>([]);

    // Data Records
    const [dataRecords, setDataRecords] = useState<DynamicObject[]>([]);

    // Search Fields
    const [search, setSearch] = useState<Map<string, string>>(new Map<string, string>());

    // Page Support Hooks
    //const [isLoading, setIsLoading] = useState(true);
    const [httpError, setHttpError] = useState(null);

    // Pagination Hooks:
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [isOpen, setIsOpen] = useState(false);

    const [activeStatusKey, setActiveStatusKey] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadComplete, setDownloadComplete] = useState(false);
    const [shouldFadeOut, setShouldFadeOut] = useState(false);

    
    const [submitRefreshDataCancelledTransactions, setSubmitRefreshDataCancelledTransactions] = useState<boolean>(false); 

    const toggleDropdown = () => {
          setIsOpen(!isOpen);
    };

    const frontEndOnly: IFrontEndOnly = {
        listTitle: props.listTitle,
        recordTitle: props.recordTitle,
        editTableName: props.editTableName,
        editHasCompoundKey: props.editHasCompoundKey
    }

    const [queryState, setQueryState] = useState<IQueryGeneric>(props.query);
    const [sortState, setSortState] = useState<ColumnSort[]>([]);

    const [renderReady, setRenderReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [tableRenderReady, setTableRenderReady] = useState(false);



     useEffect(() => {
        setData("comms", comms);
        setData("query", queryState);   
        setData("fontEndOnly", frontEndOnly); 
        setData("modal", props.modal);     
    }, []);


    // useEffect(() => {
    
    //     const endpoints: { endpoint: string; method: "POST"; body: any }[] = [
    //         { endpoint: queryOption, method: "POST", body: dataBody(queryState) },
    //         { endpoint: schemeOption, method: "POST", body: schemaBody(props.query) }
    //     ];

    //     setIsLoading(true);

    //     fetchQueryData(endpoints, processData)
    //     .catch((error: any) => {
    //         setHttpError(error.message);
    //     })
    //     .finally(() => {
    //         setIsLoading(false);
    //     });

    //    // console.log("Effect ran", { queryState, query: props.query, submitRefreshDataCancelledTransactions });

    // }, [ queryState, refreshTable]); //submitRefreshDataCancelledTransactions, props.query,

    useEffect(() => {
        // Determine if this is initial load or table update
        const firstLoad = !renderReady;

         setTableRenderReady(false); 
        if (!firstLoad) {
            setIsTableLoading(true); // show table overlay loader
        } else {
            setIsLoading(true); // full-page loader
        }

        const endpoints: { endpoint: string; method: "POST"; body: any }[] = [
            { endpoint: queryOption, method: "POST", body: dataBody(queryState) },
            { endpoint: schemeOption, method: "POST", body: schemaBody(props.query) }
        ];

        fetchQueryData(endpoints, processData)
            .catch((error: any) => setHttpError(error.message))
            .finally(() => {
                if (firstLoad) {
                    setIsLoading(false); 
                } else {
                    //setIsTableLoading(false);
                    // Add a small delay (e.g., 150ms) before hiding table loader
                   setTableRenderReady(false);
                }
            });
    }, [queryState, refreshTable]);


    // preventing the download spinner from spinning
    useEffect(() => {
        if(responseStatus === "400" || responseStatus === "401"){
            setDownloading(false);
        }
        if(showErrorAlert){
            setDownloading(false);
        }        
    }, [responseStatus, showErrorAlert])

    useEffect(() => {
        if (downloadComplete && !downloading) {
            // Start fading after 4.5s
            const fadeTimer = setTimeout(() => {
                setShouldFadeOut(true);
            }, 4500);

            // Hide alert completely after 5s
            const hideTimer = setTimeout(() => {
                setDownloadComplete(false);
                setShouldFadeOut(false); // reset for future
            }, 5000);

            return () => {
                clearTimeout(fadeTimer);
                clearTimeout(hideTimer);
            };
        }
    }, [downloadComplete, downloading]);


    useEffect(() => {
        if (sortState.length === 0) {
            // No sorting — revert to baseline query, but keep filters if any
            setQueryState(prev => ({
                ...props.query,
                where: prev.where, // preserve current filters (status/search)
                pageNum: 0,
            }));
            return;
        }

        // Sorting active — build new ORDER BY
        const updatedOrderBy = buildOrderBy(props.query.orderBy, sortState);

        // Preserve filters (where) and rebuild orderBy
        setQueryState(prev => ({
            ...props.query,
            where: prev.where,          // keep filters from legend/search
            orderBy: updatedOrderBy,    // rebuild sorting
            pageNum: 0,
        }));
    }, [sortState]);

    useEffect(() => {
        // Only mark as ready when data has actually been set
        if (!isLoading && dataRecords.length > 0) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setRenderReady(true);
                });
            });
        }
    }, [isLoading, dataRecords]);


    // const processData = (result: any) => {
    // if (result && result.length === 2) {
    //     const columnDetails: IQueryTableColumnDetails[] = result[1];
    //     const searchColumns: Map<string, string> = search;

    //     // Initialize search map if empty
    //     if (search.size === 0) {
    //         for (const key in columnDetails) {
    //             const columnDefinition: IQueryTableColumnDetails = columnDetails[key];
    //             searchColumns.set(columnDefinition.name as string, "");
    //         }
    //     }

    //     // Build processed record objects
    //     const data = result[0].data;
    //     const processedRecords: DynamicObject[] = [];

    //     for (const key in data) {
    //         const record = data[key];

    //         const recordData: DynamicObject = record.data;       // payload
    //         const recordControl: DynamicObject = record.control; // metadata
    //         const recordExtra: DynamicObject = {
    //             id: record.id,
    //             key: record.key,
    //             action: record.action,
    //             success: record.success,
    //         };

    //         const concatRecord = { ...recordData, ...recordControl, ...recordExtra };
    //         processedRecords.push(concatRecord);
    //     }

    //     // --- FIX: Support backend + custom columns (like "progress") ---
    //     const orderedColumns =
    //         Array.isArray(props.orderColumns) && props.orderColumns.length > 0
    //             ? props.orderColumns.map((col) => {
    //                   const found = columnDetails.find((detail) => detail.name === col.name);

    //                   if (found) return found; // real backend column

    //                   // custom column fallback
    //                  return {
    //                     name: col.name,
    //                     dataType: col.type || "VARCHAR",
    //                     precision: 0,
    //                     scale: 0,
    //                     isId: false,
    //                     isKey: false,
    //                     isControl: false,
    //                     isData: true,
    //                     isNullable: true,
    //                     hasDefault: false,
    //                     isCustom: true,
    //                 } as IQueryTableColumnDetails;
    //               })
    //             : columnDetails;

    //             // --- FIX: Align search map with new column order including custom columns ---
    //             const orderedSearch =
    //                 Array.isArray(props.orderColumns) && props.orderColumns.length > 0
    //                     ? new Map(
    //                         props.orderColumns.map((col) => [
    //                             col.name,
    //                             searchColumns.get(col.name) ?? "",
    //                         ])
    //                     )
    //                     : searchColumns;

    //         // console.log("This is what the columns look like?");
    //         // console.log(orderedColumns);

    //         // apply ordered data
    //         setColumnDetails(orderedColumns);
    //         setDataRecords(processedRecords);
    //         setSearch(orderedSearch);

    //         // pagination
    //         setPageNumber(result[0].pageNum);
    //         setPageSize(result[0].pageSize);
    //         setTotalRecords(result[0].numRecords);
    //         setTotalPages(Math.ceil(result[0].numRecords / result[0].pageSize));
    //     }
    // };

    const processData = (result: any) => {
        if (!result || !Array.isArray(result) || result.length < 2) return;

        // Always normalize columnDetails to an array
        const rawColumns = Array.isArray(result[1]) ? result[1] : [];
        const columnDetails: IQueryTableColumnDetails[] = rawColumns;

        // Normalize search map
        const searchColumns: Map<string, string> = new Map(search);

        // --- SAFE search initialization ---
        if (searchColumns.size === 0 && columnDetails.length > 0) {
            for (const col of columnDetails) {
                const name = typeof col?.name === "string" ? col.name : String(col?.name ?? "");
                searchColumns.set(name, "");
            }
        }

        // --- safe data extraction ---
        const data = result?.[0]?.data ?? {};
        const processedRecords: DynamicObject[] = [];

        for (const key in data) {
            const record = data[key] ?? {};
            const recordData: DynamicObject = record.data ?? {};
            const recordControl: DynamicObject = record.control ?? {};
            const recordExtra: DynamicObject = {
                id: record.id,
                key: record.key,
                action: record.action,
                success: record.success,
            };

            processedRecords.push({ ...recordData, ...recordControl, ...recordExtra });
        }

        // Helper: safe lowercase compare
        const safeLower = (v: any) => (typeof v === "string" ? v.toLowerCase() : "");

        // --- SAFE ordered columns ---
        const orderedColumns =
            Array.isArray(props.orderColumns) && props.orderColumns.length > 0
                ? props.orderColumns.map((col) => {
                    const colName =
                        typeof col?.name === "string" ? col.name : String(col?.name ?? "");

                    // safe .find()
                    const found = columnDetails.find(
                        (detail) =>
                            typeof detail?.name === "string" &&
                            safeLower(detail.name) === safeLower(colName)
                    );

                    if (found) return found;

                    // fallback custom column
                    return {
                        name: colName,
                        dataType: col.type || "VARCHAR",
                        precision: 0,
                        scale: 0,
                        isId: false,
                        isKey: false,
                        isControl: false,
                        isData: true,
                        isNullable: true,
                        hasDefault: false
                    } as IQueryTableColumnDetails;
                })
                : columnDetails;

        // --- SAFE ordered search map ---
        const orderedSearch =
            Array.isArray(props.orderColumns) && props.orderColumns.length > 0
                ? new Map(
                    props.orderColumns.map((col) => {
                        const colName =
                            typeof col?.name === "string" ? col.name : String(col?.name ?? "");
                        return [colName, searchColumns.get(colName) ?? ""];
                    })
                )
                : searchColumns;

        // Apply
        setColumnDetails(orderedColumns);
        setDataRecords(processedRecords);
        setSearch(orderedSearch);

        // Pagination (safe)
        const meta = result[0] ?? {};
        const total = meta.numRecords ?? 0;
        const size = meta.pageSize ?? 1;

        setPageNumber(meta.pageNum ?? 1);
        setPageSize(size);
        setTotalRecords(total);
        setTotalPages(Math.ceil(total / size));

        // MOST IMPORTANT PART:
        // Allow React to render the rows FIRST before hiding loader
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setRenderReady(true);
            });
        });

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setTableRenderReady(true);
            });
        });
    };



    // const processData = (result: any) => {

    //     if(result && result.length === 2){

    //         const columnDetails: IQueryTableColumnDetails[] = result[1];
    //         const searchColumns: Map<string, string> = search;


    //          if(search.size === 0){
    //             for(const key in columnDetails) {
    //                 const columnDefinition:IQueryTableColumnDetails = columnDetails[key];
    //                 searchColumns.set(columnDefinition.name as string, '');
    //             }
    //         }

    //         const data = result[0].data;
    //         const processedRecords: DynamicObject[] = [];

    //         for (const key in data) {
    //             const record = data[key];

    //             const recordData: DynamicObject = record.data;         // payload
    //             const recordControl: DynamicObject = record.control;   // metadata
    //             const recordExtra: DynamicObject = { id: record.id, key: record.key,  action: record.action, success: record.success,};

    //             // Merge all together
    //             const concatRecord = { ...recordData, ...recordControl, ...recordExtra };

    //             processedRecords.push(concatRecord);
    //         }

    //         // reorder and filter columns based on props.orderColumns
    //         const orderedColumns =
    //             Array.isArray(props.orderColumns) && props.orderColumns.length > 0
    //                 ? props.orderColumns
    //                     .map((col) =>
    //                         columnDetails.find((detail) => detail.name === col.name)
    //                     )
    //                     .filter((col): col is IQueryTableColumnDetails => !!col) // remove undefined/null
    //                 : columnDetails; // keep all columns if orderColumns is null/empty

    //         // reorder and filter search map to match props.orderColumns
    //         const orderedSearch =
    //             Array.isArray(props.orderColumns) && props.orderColumns.length > 0
    //                 ? new Map(
    //                     props.orderColumns
    //                         .filter((col) => searchColumns.has(col.name)) // only include existing keys
    //                         .map((col) => [col.name, searchColumns.get(col.name) ?? ""]) // preserve values
    //                 )
    //                 : searchColumns; // keep full search map if orderColumns is null/empty

    //         console.log(`This is what the columns look like?`); 
    //         console.log(orderedColumns);

    //         // apply ordered data
    //         //console.log(orderedColumns)
    //         setColumnDetails(orderedColumns);
    //         setDataRecords(processedRecords);
    //         setSearch(orderedSearch);

    //         // pagination 
    //         setPageNumber(result[0].pageNum);
    //         setPageSize(result[0].pageSize);
    //         setTotalRecords(result[0].numRecords);
    //         setTotalPages(Math.ceil(result[0].numRecords / result[0].pageSize));
    //     }
    // }

    //fetching query data 
   
    const fetchQueryData = async ( endpoints: { endpoint: string, method: "GET" | "POST" | "PUT" | "DELETE", body?: any }[], processData: (result?: any) => void) => {
       fetchDataMultiAdvanced(endpoints, processData); // forward endpoints and processData
    };


    // if(isLoading) {
    //     return(
    //         <Loading />
    //     )
    // }

    // if(httpError) {
    //     return(
    //         <Error error={httpError} />
    //     )
    // }

    function buildOrderBy( existingOrderBy: string | null, sortState: ColumnSort[]): string | null {
      const userSorts = sortState.filter((s) => s.direction !== null).map((s) => `${s.column} ${s.direction!.toUpperCase()}`);

      if (!userSorts.length) return existingOrderBy ?? null;

        const existingParts = existingOrderBy
            ? existingOrderBy
                .split(",")
                .map((p) => p.trim())
                .filter(Boolean)
            : [];

        const filteredExisting = existingParts.filter(
            (part) =>
            !userSorts.some((s) => part.toLowerCase().startsWith(s.split(" ")[0].toLowerCase()))
        );

        const combined = [...filteredExisting, ...userSorts];

        return combined.length ? combined.join(", ") : null;
    }


    // handling of pagination paging
    const handlePageClick = (pageNum: number) => {  
        const query = {...queryState};
        query.pageNum = pageNum;
        setQueryState(query);
    }

    // handling the page siging indexing
    const handleSizeClick = (event: React.FocusEvent<HTMLInputElement>) => {
        const query = {...queryState};
        query.pageSize = Number(event.target.value) ?? 20;
        setQueryState(query);
    } 


    // Function to handle date parsing and formatting
    function parseDate(value: string): string {
        let date: Date | null = null;

        // Try to parse the value in various formats
        const datePatterns = [
            /^\d{2}-\d{2}-\d{4}$/, // dd-mm-yyyy
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2} [APM]{2}$/, // dd-mm-yyyy hh:mm:ss AM/PM
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/, // dd-mm-yyyy hh:mm:ss
            /^\d{4}-\d{2}-\d{2}$/, // yyyy-mm-dd (valid input)
        ];

        // Check if the value matches any of the patterns
        if (datePatterns[0].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [day, month, year] = value.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[1].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' '); 
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[2].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' ');
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[3].test(value)) {
            // Valid yyyy-mm-dd, no changes needed
            date = new Date(value);
        } else {
            // If no pattern matches, return the original value
            return value;
        }

        // If date is valid, format it as yyyy-mm-dd
        if (date && !isNaN(date.getTime())) {
            return date.toISOString().split('T')[0]; // Format as yyyy-mm-dd
        }

        // If invalid date, return the original value
        return value;
    }

    // function for building where clause
    const buildWhereClause = ( search: Map<string, string>, orderColumns: { name: string; type: string }[]): string | null => {
       const conditions: string[] = [];

        search.forEach((value, column) => {
            if (!value || !value.length) return;
            
            const colDef = orderColumns.find((col) => col.name === column);
            if (!colDef) return;
            

            const colType = colDef.type.toUpperCase();

            if (colType === "TIMESTAMP" || colType === "DATE") {
                const formatted = parseDate(value);
                if (formatted) {
                    conditions.push(`${column} = '${formatted}'`);
                }
            } else if (
                colType === "INTEGER" ||
                colType === "BIGINT" ||
                colType === "DECIMAL" ||
                colType === "NUMERIC"
            ){
                const numericValue = Number(value);
                if (!isNaN(numericValue)) {
                    conditions.push(`${column} = ${numericValue}`);
                }
            } else {
                // Default string comparison
                conditions.push(`${column} LIKE '%${value}%'`);
            }
        });

        return conditions.length > 0 ? conditions.join(" AND ") : null;
    }


    const searchClicked = () => {
        const whereClause = buildWhereClause(search, props.orderColumns ?? []);
        const hasSorting = sortState.length > 0;

        if (!hasSorting) {
            // No sorting — just apply the search filters to the original query
            setQueryState({
                ...props.query,
                where: whereClause,
                pageNum: 0,
            });
        } else {
            // Sorting active — rebuild orderBy but base on original query
            const updatedOrderBy = buildOrderBy(props.query.orderBy, sortState);

            setQueryState({
                ...props.query,
                where: whereClause,
                orderBy: updatedOrderBy,
                pageNum: 0,
            });
        }

        setActiveStatusKey(null);
    };


    const clearClicked = () => {

        const clearedSearch = new Map<string, string>();

        if (props.orderColumns) {
            props.orderColumns.forEach(col => {
            clearedSearch.set(col.name, "");
            });
        }

        // If user has active sorts, keep them
        // otherwise, fully revert to the original query
        if (sortState.length === 0) {
            //console.log("No sorting — reverting to full original query.");
            setQueryState(props.query);
         } else {
            // Sorting is active — preserve it, but clear filters
            // console.log("Sorting active — rebuilding orderBy only.");
            const updatedOrderBy = buildOrderBy(props.query.orderBy, sortState);
            setQueryState({
                ...props.query,        // base everything on the original query
                orderBy: updatedOrderBy, // only update orderBy
            });
        }

        setSearch(clearedSearch);
        setActiveStatusKey(null);
    };



    const renderStatusLegend = () => {

            if (!props.hasIntegrationStatusLegend) return null;
          
            return (
              <div className="position-relative integration-status-legend">
                <button onClick={toggleDropdown} className="btn-md btn btn-secondary d-flex align-items-center">
                  Integration Statuses
                  <svg xmlns="http://www.w3.org/2000/svg"  width="16" height="16" viewBox="0 0 24 24" fill="white" style={{ verticalAlign: 'middle' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm2 14h-4v-2h1v-4h-1v-2h3v6h1v2z" />
                  </svg>
                </button>
                { isOpen && (
                  <div className="integration-status-legend-content">
                    {props.integrationstatusLegend?.map((item, index) => (
                      <div className="item-rows" key={index} >
                        <div style={{ width: '1rem',  height: '1rem', backgroundColor: item.color, borderRadius: '0.125rem' }} />
                        <p className="status-name">
                          {item.status}:
                        </p>
                        <p className="status-description">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
        );
    }


    const handleFilterByStatusChange = (status: string | null) => {
        // Map of status configurations per table
        const tableStatusMap: Record<string,{ fullColumnName: string; statuses: Record<string, string[]> }> = {
            sa_m24l_stock_adjustment: {
                fullColumnName: "status",
                statuses: {
                    ERROR: ["ERROR"],
                    CANCELLED: ["CANCELLED"],
                    PROCESSING: ["TO_PROCESS"],
                    CLOSED: ["PROCESSED"],
                },
            },
            sa_m24l_return_request: {
                fullColumnName: "integration_status",
                statuses: {
                    ERROR: ["ERROR"],
                    CANCELLED: ["CANCELLED"],
                    PROCESSING: ["TO_SEND", "SENT", "ARRIVED", "SUCCESS"],
                    CLOSED: ["CLOSED"],
                },
            },
            sa_m24l_item: {
                fullColumnName: "integration_status",
                statuses: {
                    ERROR: ["ERROR"],
                    CANCELLED: ["CANCELLED"],
                    PROCESSING: ["TO_SEND", "SENT"],
                    CLOSED: ["SUCCESS"],
                },
            },
            sa_m24l_stock_movement_in: {
                fullColumnName: "integration_status",
                statuses: {
                    ERROR: ["ERROR"],
                    CANCELLED: ["CANCELLED"],
                    PROCESSING: ["TO_SEND", "ARRIVED", "SENT", "SUCCESS", "TO_CLOSE"],
                    CLOSED: ["CLOSED"],
                },
            },
            sa_m24l_stock_movement_out: {
                fullColumnName: "integration_status",
                statuses: {
                    ERROR: ["ERROR"],
                    CANCELLED: ["CANCELLED"],
                    PROCESSING: [
                        "TO_SEND",
                        "PICKING",
                        "DISPATCHED",
                        "SENT",
                        "SUCCESS",
                        "TO_CLOSE",
                    ],
                    CLOSED: ["CLOSED"],
                },
            },
            sa_m24l_pick_list: {
                fullColumnName: "integration_status",
                statuses: {
                    ERROR: ["ERROR"],
                    CANCELLED: ["CANCELLED"],
                    PROCESSING: ["TO_SEND", "PICKING", "DISPATCHED", "SENT", "SUCCESS"],
                    CLOSED: ["CLOSED"],
                },
            },
        };

        // Fallback configuration
        const tableConfig = tableStatusMap[props.tableName];
        const fullColumn = tableConfig?.fullColumnName ?? "integration_status";
        const statusValues =
            status && tableConfig ? tableConfig.statuses[status] ?? [] : [];

        // Build WHERE clause
        const conditions: string[] = [];

        // Add status filter
        if (statusValues.length > 0) {
            const quotedValues = statusValues.map((v) => `'${v}'`).join(", ");
            conditions.push(`${fullColumn} IN (${quotedValues})`);
        }

        // Add search filters
        search.forEach((value, column) => {
            if (value && value.length && column !== fullColumn) {
            conditions.push(`${column} LIKE '%${value}%'`);
            }
        });

        const whereClause = conditions.length > 0 ? conditions.join(" AND ") : null;

        // Combine with sortState
        const hasSorting = sortState.length > 0;
        const updatedOrderBy = hasSorting
            ? buildOrderBy(props.query.orderBy, sortState)
            : props.query.orderBy;

        // Update query
        setQueryState({
            ...props.query,   // always base on original query
            where: whereClause,
            orderBy: updatedOrderBy,
            pageNum: 0,
        });

        setActiveStatusKey(status);
        };


    const handleTableExport = () => {
        if(props.reportOptions?.reportQuery){

            // construct the endpoint for export of report 
            const export_url = `${exportOption}?templateFilename=${props.reportOptions?.templateName}&reportFilename=${props.reportOptions?.reportName}`; 
            const request_body = { 
                    where: queryState.where,
                    orderBy: queryState.orderBy,
                    pageNum: 0,
                    pageSize: 0,
                    data: props.reportOptions?.reportData
            }

            // console.log(`=================================`);
            // console.log(export_url);

            //  console.log(request_body);
            // console.log(`=================================`);
        
            setDownloading(true); 
            setDownloadComplete(false);

            fetchData(export_url, 
            {method:"POST", body: JSON.stringify(request_body)}, 
            (result: any, status: string) => {
                if (status === "200" || status === "201") {
                    handleExcelExport(result);
                    setDownloadComplete(true);  // change banner to "downloaded!"
                    setDownloading(false);      // hide "downloading..."
                } else {
                    setDownloading(false);      // hide "downloading..."
                    setDownloadComplete(false); // hide success banner if error
                }
            });
        }else{
            return;
        }
    }
        
    const handleExcelExport = async (result: any) => {

        // Use the abbreviated name for both the template and report
        const fileName = `${props.reportOptions?.reportName}`; // Use abbreviated table name

        // Create a download link for the Blob
        const url = URL.createObjectURL(result);
        // Create a temporary <a> element to trigger the download
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName; // Set the default filename here
        document.body.appendChild(a);
        a.click();
    }



    return (
        <>
             {(getData("primaryKeyValue") === undefined) 
                ?
                    <>
                        { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 

                         {/* Show only while downloading and not errored */}
                        {downloading && !downloadComplete && !showErrorAlert && (
                        <div className="alert d-flex align-items-center py-2 px-3 mb-2" role="alert" style={{ backgroundColor: "#e6eefa", border: '1px solid rgb(222, 235, 237)', color: "#035a70" }}>
                            <span className="flex-grow-1 text-center">
                            Your report is currently being processed. Please wait...
                            </span>
                        </div>
                        )}

                        {/* Show success only if download succeeded */}
                        {downloadComplete && !downloading && !showErrorAlert && (
                        <div className={`alert d-flex align-items-center py-2 px-3 mb-2 ${shouldFadeOut ? 'fading-out' : ''}`} role="alert" 
                            style={{ backgroundColor: "#c7f5bf", border: "1px solid rgb(222, 235, 237)", color: "#037012", transition: "opacity 0.5s ease-in-out", opacity: shouldFadeOut ? 0 : 1 }}>
                            <span className="flex-grow-1 text-center">
                            Your report has been successfully downloaded.
                            </span>
                            <button type="button" 
                            style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', backgroundColor: '#c7f5bf', color: '#e53e3e', borderRadius: '8px', padding: '6px', height: '32px', width: '32px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                            aria-label="Close"
                            onClick={() => setDownloadComplete(false)} 
                            >
                            <svg style={{ width: '12px', height: '12px' }} aria-hidden="true" xmlns="http://www.w3.org/2000/svg"  fill="none" viewBox="0 0 14 14">
                                <path stroke="#037012" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                            </svg>
                            </button>
                        </div>
                        )}

                        {(!renderReady) && <Loading />}

                        {renderReady && (
                        <Card>
                            <Card.Header className="pb-2">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                             <Card.Title>{props.listTitle}</Card.Title>
                             <div className="d-flex align-items-center gap-2">
                                   { /* export availability */ }
                                   { props.reportOptions &&
                                    <div title={downloading ? 'Preparing report...' : 'Export'}>
                                        <button
                                            className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                            onClick={handleTableExport}
                                            disabled={downloading}
                                            style={{
                                                position: 'relative',
                                                padding: downloading ? '0.39rem 0.65rem' : '0.4rem 0.65rem', 
                                            }}
                                        >
                                            {downloading ? (
                                                
                                                <span className="spinner-border spinner-border-sm custom-spinner" role="status" aria-hidden="true"></span>
                                            ) : (
                                                <Download size={16} />
                                            )}
                                        </button>
                                    </div>
                                    }
                                    {renderStatusLegend()}

                                    {/* Optional section for failed transactions */}
                                    {props.failedTransactionsSection && (
                                    <>
                                        {props.failedTransactionsSection.toggleHeader}
                                    </>
                                    )}

                                </div>
                                
                              </div>

                                { /* Positioing the failed transactions table */ }
                                { props.failedTransactionsSection && (
                                    <>
                                    {props.failedTransactionsSection.bodyContents}
                                    </>
                                )}
                                
                                { /* custom header controls */ }
                                {props.headerControls && (
                                    <div className="d-flex flex-column ">
                                        {props.headerControls}
                                    </div>
                                )}
                               

                                { /* pagination */ }
                                <ListControl 
                                  pageNumber={pageNumber} 
                                  pageSize={pageSize} 
                                  totalRecords={totalRecords} 
                                  totalPages={totalPages} 
                                  pageClick={handlePageClick} 
                                  sizeClick={handleSizeClick} 
                                  handleFilterByStatusChange={handleFilterByStatusChange} 
                                  activeStatusKey={activeStatusKey}
                                  onStatusChange={setActiveStatusKey}
                                  hasFilterByStatus={props.hasIntegrationStatusLegend ?? null}
                                  />
                            </Card.Header>

                            <Card.Body className="pt-0">
                                <div className="sa-table-container" >
                                <Table size="md" hover className="sa-table">
                                    <ListSearch 
                                     search={search} 
                                     setSearch={setSearch} 
                                     searchClicked={searchClicked} 
                                     clearClicked={clearClicked} 
                                     searchableColumns={props.orderColumns}
                                     />
                                    <ListColumns 
                                     sortState={sortState} 
                                     setSortState={setSortState} 
                                     columnDetails={columnDetails} 
                                     sortableColumns={props.orderColumns}
                                     />

                                    {isTableLoading && !tableRenderReady && (props.orderColumns?.length ?? 0) > 0 ? (
                                        <TableLoader colCount={(props.orderColumns?.length ?? 0) + 1} />
                                    ) : (
                                        <ListRecords 
                                        columnDetails={columnDetails} 
                                        dataRecords={dataRecords} 
                                        errorCategoryName={props.errorCategoryName} 
                                        tableName={props.tableName} 
                                        renderRowData={props.renderRowData}
                                        renderActions={props.renderActions}
                                    />
                                    )}
                                </Table>
                                </div>
                            </Card.Body>
                        </Card>     
                        )}               
                    </>
                :                            
                    <>
                        <Card> 
                            <Card.Header>
                                <Card.Title>{props.listTitle}</Card.Title>
                            </Card.Header>

                            
                        </Card>
                    </>
                }
            
        </>
    );
    
}

export function formatDate(dateString: string): string {
    var theDate = new Date(dateString);
    return theDate.toLocaleDateString().replaceAll("\/", "-") + " " + theDate.toLocaleTimeString();
}
